// Ejercicios fiscales, períodos, cierre de resultados y apertura.
// Contabilidad → Movimientos → Períodos y Cierre.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarRange, Loader2, Lock, Unlock, PlayCircle, Plus, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { useTranslation } from 'react-i18next';
import { usd, rpc } from './shared';

interface Periodo { id: string; mes: number; estado: string; asientos: number }
interface EstadoPeriodo {
  id: string; excepciones: number;
  cerrado_at: string | null; cerrado_por: string | null;
  reabierto_at: string | null; reabierto_por: string | null; motivo_reapertura: string | null;
  reaperturas: number;
  // null = nunca se cerró (no es «intacto», es «sin sello»)
  intacto: boolean | null;
}
interface Ejercicio { id: string; anio: number; fecha_inicio: string; fecha_fin: string; estado: string; periodos: Periodo[] }

export function PeriodosCierre() {
  const { t } = useTranslation('contabilidad');
  const mesN = (m: number) => t(`meses.${m}`);
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: ejercicios = [], isFetching } = useQuery<Ejercicio[]>({ queryKey: ['cont-ejercicios'], queryFn: () => rpc<Ejercicio[]>('cont_ejercicios') });
  // Cuántos documentos de cada mes NO llegaron al libro. Cerrar un mes con
  // documentos fuera lo deja mal para siempre, así que hay que verlo ANTES de
  // pulsar el candado, no después.
  const { data: estados = [] } = useQuery<EstadoPeriodo[]>({
    queryKey: ['cont-periodos-estado'], queryFn: () => rpc('cont_periodos_estado', {}),
  });
  const bloqueantes = new Map(estados.map((e) => [e.id, Number(e.excepciones) || 0]));
  // El sello del cierre (script 779): dice si el mes se tocó DESPUÉS de cerrarlo.
  const sello = new Map(estados.map((e) => [e.id, e]));
  const inval = () => { void qc.invalidateQueries({ queryKey: ['cont-ejercicios'] }); void qc.invalidateQueries({ queryKey: ['cont-asientos'] }); };
  const [anio, setAnio] = useState(new Date().getFullYear());

  const crear = useMutation({ mutationFn: () => rpc('cont_ejercicio_crear', { _anio: anio }), onSuccess: () => { toast.success(t('periodos.ej_creado', { anio })); inval(); }, onError: (e) => toast.error(getErrorMessage(e)) });
  const periodo = useMutation({
    mutationFn: (v: { id: string; cerrar: boolean; motivo?: string }) =>
      rpc<{ hash: string; asientos: number }>('cont_periodo_estado',
        { _periodo_id: v.id, _cerrar: v.cerrar, _motivo: v.motivo ?? null }),
    onSuccess: (r, v) => {
      if (v.cerrar) toast.success(t('periodos.periodo_cerrado', { n: r.asientos }));
      else toast.success(t('periodos.periodo_reabierto'));
      inval();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const cerrar = useMutation({ mutationFn: (id: string) => rpc<{ utilidad: number }>('cont_cierre_resultados', { _ejercicio_id: id }), onSuccess: (r) => { toast.success(t('periodos.ej_cerrado', { valor: usd(r.utilidad) })); inval(); }, onError: (e) => toast.error(getErrorMessage(e)) });
  const reabrir = useMutation({
    mutationFn: (v: { id: string; motivo: string }) =>
      rpc('cont_ejercicio_reabrir', { _ejercicio_id: v.id, _motivo: v.motivo }),
    onSuccess: () => { toast.success(t('periodos.ej_reabierto')); inval(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const apertura = useMutation({ mutationFn: (id: string) => rpc('cont_apertura_ejercicio', { _ejercicio_destino: id }), onSuccess: () => { toast.success(t('periodos.apertura_generada')); inval(); }, onError: (e) => toast.error(getErrorMessage(e)) });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-xl font-semibold flex items-center gap-2"><CalendarRange className="w-5 h-5" /> {t('periodos.titulo')}</h1>
        <div className="flex items-end gap-2">
          <label className="flex flex-col text-xs text-muted-foreground">{t('periodos.anio')}
            <input type="number" className="flex h-9 w-28 rounded-md border border-input bg-background px-2 text-sm mt-1" value={anio} onChange={(e) => setAnio(Number(e.target.value))} /></label>
          <Button isLoading={crear.isPending} onClick={() => crear.mutate()}><Plus className="w-4 h-4 mr-1" /> {t('periodos.crear_ejercicio')}</Button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground">{t('periodos.ayuda')}</p>

      {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
      {ejercicios.length === 0 && !isFetching && <p className="text-sm text-muted-foreground">{t('periodos.sin_ejercicios')}</p>}

      {ejercicios.map((e) => (
        <Card key={e.id}>
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              {t('periodos.ejercicio_n', { anio: e.anio })}
              <span className={`rounded-full px-2 py-0.5 text-xs ${e.estado === 'abierto' ? 'bg-estable-soft text-estable' : 'bg-muted text-muted-foreground'}`}>{t(`periodos.estado_${e.estado}`, { defaultValue: e.estado })}</span>
            </CardTitle>
            <div className="flex gap-2">
              {e.estado === 'abierto' ? <>
                <Button size="sm" variant="outline" isLoading={apertura.isPending} onClick={() => apertura.mutate(e.id)} title={t('periodos.apertura_tip')}><PlayCircle className="w-4 h-4 mr-1" /> {t('periodos.apertura')}</Button>
                <Button size="sm" isLoading={cerrar.isPending} onClick={async () => { if (await dlg.confirmar(t('periodos.confirmar_cerrar_ej', { anio: e.anio }), { detalle: t('periodos.detalle_cerrar_ej'), peligro: true })) cerrar.mutate(e.id); }}><Lock className="w-4 h-4 mr-1" /> {t('periodos.cerrar_ejercicio')}</Button>
              </> : <Button size="sm" variant="outline" isLoading={reabrir.isPending} onClick={async () => {
                const v = await dlg.pedir(t('periodos.reabrir_ejercicio', { anio: e.anio }), [
                  { key: 'motivo', label: t('periodos.motivo_reapertura'), multilinea: true, minimo: 3 },
                ], { detalle: t('periodos.detalle_reabrir_ej'), aceptar: t('periodos.reabrir') });
                if (!v) return;
                reabrir.mutate({ id: e.id, motivo: v.motivo });
              }}><RotateCcw className="w-4 h-4 mr-1" /> {t('periodos.reabrir')}</Button>}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {e.periodos.map((p) => (
                <button key={p.id} disabled={e.estado === 'cerrado' || periodo.isPending}
                  onClick={async () => {
                    const pend = bloqueantes.get(p.id) ?? 0;
                    if (p.estado === 'abierto') {
                      if (pend > 0 && !(await dlg.confirmar(t('periodos.docs_sin_libro', { mes: mesN(p.mes), n: pend }), { detalle: t('periodos.detalle_cerrar_mes'), peligro: true }))) return;
                      periodo.mutate({ id: p.id, cerrar: true });
                      return;
                    }
                    // Reabrir exige motivo, y queda firmado con tu usuario en la
                    // bitácora del ejercicio. Es lo que hace auditable el corte.
                    const v = await dlg.pedir(t('periodos.reabrir_mes', { mes: mesN(p.mes) }), [
                      { key: 'motivo', label: t('periodos.motivo_reapertura'), multilinea: true, minimo: 3 },
                    ], { detalle: t('periodos.detalle_reabrir_mes'), aceptar: t('periodos.reabrir') });
                    if (!v) return;
                    periodo.mutate({ id: p.id, cerrar: false, motivo: v.motivo });
                  }}
                  className={`flex items-center justify-between gap-1 rounded-md border px-2 py-1.5 text-sm ${
                    sello.get(p.id)?.intacto === false ? 'border-red-500/50 bg-red-500/5'
                      : p.estado === 'abierto' ? 'hover:bg-muted' : 'bg-muted/60 text-muted-foreground'} disabled:opacity-60`}
                  title={(() => {
                    const s = sello.get(p.id);
                    const partes: string[] = [];
                    if (s?.cerrado_at) partes.push(`${t('periodos.tip_cerrado', { fecha: String(s.cerrado_at).slice(0, 10) })}${s.cerrado_por ? t('periodos.tip_por', { usuario: s.cerrado_por }) : ''}`);
                    if (s?.reabierto_at) partes.push(`${t('periodos.tip_reabierto', { fecha: String(s.reabierto_at).slice(0, 10) })}${s.reabierto_por ? t('periodos.tip_por', { usuario: s.reabierto_por }) : ''}${s.motivo_reapertura ? `: ${s.motivo_reapertura}` : ''}`);
                    if (s?.intacto === false) partes.push(t('periodos.tip_cambiaron'));
                    partes.push(p.estado === 'abierto' ? t('periodos.clic_cerrar') : t('periodos.clic_reabrir'));
                    return partes.join('\n');
                  })()}>
                  <span className="flex items-center gap-1">{mesN(p.mes)}
                    {(bloqueantes.get(p.id) ?? 0) > 0 && (
                      <span title={t('periodos.tip_sin_contab', { n: bloqueantes.get(p.id) })}
                        className="rounded-full bg-amber-500/15 px-1.5 text-[10px] font-semibold text-amber-600">
                        {bloqueantes.get(p.id)}
                      </span>)}
                  </span>
                  <span className="flex items-center gap-1 text-xs">
                    {(sello.get(p.id)?.reaperturas ?? 0) > 0 && (
                      <span className="rounded-full bg-blue-500/15 px-1.5 text-[10px] font-semibold text-blue-600"
                        title={t('periodos.tip_reabierto_veces', { n: sello.get(p.id)?.reaperturas })}>
                        ↻{sello.get(p.id)?.reaperturas}
                      </span>)}
                    {sello.get(p.id)?.intacto === false && (
                      <span className="text-[10px] font-semibold text-red-600"
                        title={t('periodos.tip_cambiaron_mes')}>⚠</span>)}
                    {p.asientos > 0 && <span className="text-muted-foreground">{p.asientos}</span>}
                    {p.estado === 'abierto' ? <Unlock className="w-3.5 h-3.5 text-estable" /> : <Lock className="w-3.5 h-3.5" />}
                  </span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
